import React, { useEffect, useMemo, useState } from 'react';
import { SeriesOptionsType } from 'highcharts';
import { Button } from '../../../design-system';
import { Chart } from '../../../components/chart/BaseChart';
import { ChartSpec } from '../../../components/chart/chartSpec';
import {
  buildLambdaSampleCurve,
  buildLiaoSampleCurve,
} from '../../../components/utils/curveGeneration';
import { LambdaMethod, Sample } from '../../../@types/samples/types';
import { AnalysisMethod, SampleRunResult, isKrMethod } from '../types';
import { LAMBDA_METHOD_LABELS } from '../../../components/dashboard/components/LambdaAnalysis/methodConstants';
import { pickBestLambdaMethod } from '../batchRunner';
import { getKrChartData } from '../krChartData';
import StatusPill from './StatusPill';
import styles from '../MultiAnalysisPage.module.css';
import {
  BROOKS_COREY_EXPONENT_LABEL,
  BROOKS_COREY_PORE_SIZE_INDEX_LABEL,
  toBrooksCoreyPoreSizeDistributionIndex,
} from '../../../utils/brooksCorey';

/** Solver order for the compare table. */
const SOLVER_ORDER: LambdaMethod[] = [
  'mean_slope',
  'ols_loglog',
  'nlsq',
  'weighted_nlsq',
];

const CURVE_POINTS = 60;

type Props = {
  sample: Sample;
  result?: SampleRunResult;
  /** Active batch methodology — drives the fit curve and parameter list. */
  method: AnalysisMethod;
  onPrev?: () => void;
  onNext?: () => void;
  onRerun: (uid: string) => void;
  onToggleReviewed: (uid: string) => void;
  onOpenInCapillaryPressure: (uid: string) => void;
  /** Regenerates analysis_curves.liao when the stored sample lacks them. */
  regenerateCurves: (sample: Sample) => Promise<Sample | null>;
  rerunDisabled?: boolean;
  /** Per-solver preview results for this sample (Brooks-Corey compare mode). */
  methodResults?: Partial<Record<LambdaMethod, SampleRunResult>>;
  /** Adopt a different solver's fit for this sample. */
  onChooseMethod?: (uid: string, method: LambdaMethod) => void;
};

const formatNumber = (value?: number | null, digits = 4) =>
  typeof value === 'number' && Number.isFinite(value)
    ? value.toFixed(digits)
    : '—';

/**
 * Full inspection view for one batch result: fit-vs-lab chart, the active
 * methodology's parameters, validation reasons and per-sample actions.
 */
const SampleDetailPanel = ({
  sample,
  result,
  method,
  onPrev,
  onNext,
  onRerun,
  onToggleReviewed,
  onOpenInCapillaryPressure,
  regenerateCurves,
  rerunDisabled,
  methodResults,
  onChooseMethod,
}: Props) => {
  // Local copy enriched with worker-regenerated curves when needed.
  const [enriched, setEnriched] = useState<Sample>(sample);
  const [isLog, setIsLog] = useState(false);

  useEffect(() => {
    setEnriched(sample);
    // Curve regeneration uses the Liao worker; only relevant in Liao mode.
    // Brooks-Corey batch responses carry lambda_curves, and when absent the
    // chart falls back to the raw pc_model points below.
    if (method !== 'liao') return;
    const curves = sample.analysis_curves?.liao;
    const hasScatter = Boolean(
      curves?.PceMatch?.length || curves?.Pres?.length
    );
    if (hasScatter || !sample.analysis_values?.liao) return;

    let active = true;
    regenerateCurves(sample).then(regenerated => {
      if (active && regenerated && regenerated.uid === sample.uid) {
        setEnriched(regenerated);
      }
    });
    return () => {
      active = false;
    };
  }, [sample, method, regenerateCurves]);

  const liao = enriched.analysis_values?.liao;
  const lambda = enriched.analysis_values?.lambda_values;
  const outcome = enriched.analysis_outcomes?.[method];

  // Compare mode: which solvers were fitted for this sample, and the auto-best.
  const comparedMethods = SOLVER_ORDER.filter(m =>
    Boolean(methodResults?.[m])
  );
  const bestMethod = methodResults
    ? pickBestLambdaMethod(methodResults)
    : undefined;

  const spec: ChartSpec | null = useMemo(() => {
    // Kr methodologies: lab Kro + Krw/Krg points with the smooth Corey
    // curves reconstructed from the refined (true_*) or lab parameters —
    // the same builders the Kr Analysis Refinement Curves tab uses.
    if (isKrMethod(method)) {
      const kr = getKrChartData(enriched, method, CURVE_POINTS);
      if (!kr) return null;
      // Refined curves share their lab counterpart's color (Kro orange,
      // Krw/Krg blue) so each fluid reads as one series; lab points are
      // small circles, the fit is the solid line of the same hue.
      const KRO_COLOR = '#f47936';
      const KRW_COLOR = '#4f8df9';
      const series: SeriesOptionsType[] = [
        {
          name: `${kr.decreasingLabel} Lab`,
          type: 'scatter',
          data: kr.measuredDecreasing,
          color: KRO_COLOR,
          marker: { radius: 2.5, symbol: 'circle' },
          zIndex: 2,
        },
        {
          name: `${kr.increasingLabel} Lab`,
          type: 'scatter',
          data: kr.measuredIncreasing,
          color: KRW_COLOR,
          marker: { radius: 2.5, symbol: 'circle' },
          zIndex: 2,
        },
        {
          name: `${kr.decreasingLabel} ${kr.refined ? 'Refined' : 'Calc'}`,
          type: 'spline',
          data: kr.curveDecreasing,
          color: KRO_COLOR,
          marker: { enabled: false },
          lineWidth: 2,
          enableMouseTracking: false,
          zIndex: 1,
        },
        {
          name: `${kr.increasingLabel} ${kr.refined ? 'Refined' : 'Calc'}`,
          type: 'spline',
          data: kr.curveIncreasing,
          color: KRW_COLOR,
          marker: { enabled: false },
          lineWidth: 2,
          enableMouseTracking: false,
          zIndex: 1,
        },
      ];
      return {
        height: '52%',
        title: `Kr x ${kr.xLabel}`,
        xAxis: {
          title:
            kr.xLabel === 'Sw' ? 'Water Saturation' : 'Gas Saturation',
          type: 'linear' as const,
        },
        yAxis: {
          title: 'Relative Permeability',
          type: isLog ? ('logarithmic' as const) : ('linear' as const),
          ...(isLog ? { min: 0.001, max: 1 } : {}),
        },
        series,
      };
    }

    // Scatter: corrected Sw × reservoir Pc from the active methodology's
    // curves, falling back to the other methodology's, then raw pc_model.
    // In lambda mode each point carries whether it belonged to the fitted
    // subset (`lambda_curves.indexes`) so excluded points render as open
    // circles, mirroring the single-sample editor.
    const liaoCurves = enriched.analysis_curves?.liao;
    const lambdaCurves = enriched.analysis_curves?.lambda_curves;
    const selectedIndexes =
      method === 'lambda' ? lambdaCurves?.indexes ?? null : null;
    const outlierIndexes =
      method === 'lambda'
        ? enriched.analysis_outcomes?.lambda?.internal_excluded_indexes ?? null
        : null;
    const candidates =
      method === 'lambda'
        ? [
            {
              pc: lambdaCurves?.Pres,
              sw: lambdaCurves?.CorrectedSw,
              withSelection: true,
            },
            {
              pc: liaoCurves?.PceMatch ?? liaoCurves?.Pres,
              sw: liaoCurves?.CorrectedSw,
              withSelection: false,
            },
          ]
        : [
            {
              pc: liaoCurves?.PceMatch ?? liaoCurves?.Pres,
              sw: liaoCurves?.CorrectedSw,
              withSelection: false,
            },
            {
              pc: lambdaCurves?.Pres,
              sw: lambdaCurves?.CorrectedSw,
              withSelection: false,
            },
          ];

    let original: { x: number; y: number }[] = [];
    let notSelected: { x: number; y: number }[] = [];
    let outliers: { x: number; y: number }[] = [];
    for (const candidate of candidates) {
      const selected: { x: number; y: number }[] = [];
      const excluded: { x: number; y: number }[] = [];
      const excludedOutliers: { x: number; y: number }[] = [];
      (candidate.pc ?? []).forEach((pc, index) => {
        const sw = candidate.sw?.[index];
        if (pc == null || sw == null) return;
        const point = { x: sw, y: pc };
        const isSelected =
          !candidate.withSelection ||
          !selectedIndexes ||
          selectedIndexes.includes(index);
        if (isSelected) {
          selected.push(point);
        } else if (
          candidate.withSelection &&
          outlierIndexes &&
          outlierIndexes.includes(index)
        ) {
          excludedOutliers.push(point);
        } else {
          excluded.push(point);
        }
      });
      if (
        selected.length > 0 ||
        excluded.length > 0 ||
        excludedOutliers.length > 0
      ) {
        original = selected;
        notSelected = excluded;
        outliers = excludedOutliers;
        break;
      }
    }
    if (
      original.length === 0 &&
      notSelected.length === 0 &&
      outliers.length === 0
    ) {
      const model = enriched.input_sample_curves?.pc_model;
      (model?.pc ?? []).forEach((pc, index) => {
        const sw = model?.sw?.[index];
        if (pc != null && sw != null) original.push({ x: sw, y: pc });
      });
    }

    let calculated: { x: number; y: number }[] = [];
    if (method === 'lambda') {
      if (lambda) {
        // Prefer the backend-resolved swir/pc_max so the curve renders even
        // when Swi is missing (swir fell back to the minimum measured Sw).
        const derivedPcMax = [...original, ...notSelected, ...outliers].reduce(
          (max, point) => Math.max(max, point.y),
          0
        );
        calculated = buildLambdaSampleCurve(
          { ...lambda, pc_max: lambda.pc_max ?? derivedPcMax },
          lambda.swir ?? enriched.input_sample_values?.swi,
          CURVE_POINTS
        );
      }
    } else if (liao) {
      calculated = buildLiaoSampleCurve(liao, CURVE_POINTS);
    }

    if (
      original.length === 0 &&
      notSelected.length === 0 &&
      outliers.length === 0 &&
      calculated.length === 0
    ) {
      return null;
    }

    const series: SeriesOptionsType[] = [
      {
        name: 'Data SCAL',
        type: 'scatter',
        data: original,
        color: '#f47936',
        marker: { radius: 5 },
        zIndex: 2,
      },
      {
        name: method === 'lambda' ? 'Pc Brooks-Corey' : 'Pc Liao',
        type: 'spline',
        data: calculated,
        color: '#72E2D8',
        marker: { enabled: false },
        lineWidth: 2,
        enableMouseTracking: false,
        zIndex: 1,
      },
    ];

    if (notSelected.length > 0) {
      series.push({
        name: 'Data SCAL (not selected)',
        type: 'scatter',
        data: notSelected,
        color: '#f47936',
        marker: {
          radius: 5,
          fillColor: 'transparent',
          lineColor: '#f47936',
          lineWidth: 1.5,
        },
        zIndex: 2,
      });
    }

    if (outliers.length > 0) {
      series.push({
        name: 'Excluded outlier',
        type: 'scatter',
        data: outliers,
        color: '#dc2626',
        marker: {
          radius: 6,
          symbol: 'diamond',
          fillColor: 'transparent',
          lineColor: '#dc2626',
          lineWidth: 2,
        },
        zIndex: 3,
      });
    }

    return {
      height: '52%',
      title: isLog ? 'Logarithm Pc x Sw' : 'Pc x Sw',
      xAxis: {
        title: isLog ? 'Log Sw' : 'Water Saturation',
        type: isLog ? 'logarithmic' : 'linear',
        ...(isLog ? { min: 0.001, max: 1 } : {}),
      },
      yAxis: {
        title: isLog ? 'Log Pc' : 'Capillary Pressure',
        type: isLog ? 'logarithmic' : 'linear',
        ...(isLog ? { min: 0.1, max: 1000 } : {}),
      },
      series,
    };
  }, [enriched, liao, lambda, method, isLog]);

  return (
    <div className={styles.detail}>
      <div className={styles.detailHeader}>
        <div className={styles.detailNav}>
          <Button variant="ghost" size="sm" onClick={onPrev} disabled={!onPrev}>
            ← Prev
          </Button>
          <Button variant="ghost" size="sm" onClick={onNext} disabled={!onNext}>
            Next →
          </Button>
        </div>
        {result ? (
          <StatusPill status={result.status} reviewed={result.reviewed} />
        ) : null}
      </div>

      {result && result.reasons.length > 0 ? (
        <ul className={styles.detailReasons}>
          {result.reasons.map(reason => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      ) : null}

      {(method === 'lambda' || isKrMethod(method)) &&
      outcome?.selection_note ? (
        <p className={styles.detailSelectionNote}>{outcome.selection_note}</p>
      ) : null}

      {spec ? (
        <div className={styles.detailChart}>
          <div className={styles.detailScaleToggle} role="group" aria-label="Axis scale">
            <Button
              variant={isLog ? 'secondary' : 'primary'}
              size="sm"
              aria-pressed={!isLog}
              onClick={() => setIsLog(false)}
            >
              Linear
            </Button>
            <Button
              variant={isLog ? 'primary' : 'secondary'}
              size="sm"
              aria-pressed={isLog}
              onClick={() => setIsLog(true)}
            >
              Logarithmic
            </Button>
          </div>
          {/* Remount on scale/method/sample change so Highcharts rebuilds the
              axes cleanly — an in-place update keeps stale extremes otherwise. */}
          <Chart
            key={`${enriched.uid}-${method}-${isLog ? 'log' : 'linear'}`}
            spec={spec}
          />
        </div>
      ) : (
        <p className={styles.noMatches}>
          No{' '}
          {method === 'lambda'
            ? 'Brooks-Corey'
            : isKrMethod(method)
            ? 'Kr'
            : 'Liao'}{' '}
          curve data available for this sample yet. Run the batch analysis or
          rerun this sample.
        </p>
      )}

      {method === 'lambda' && comparedMethods.length > 0 ? (
        <div className={styles.solverCompare}>
          <div className={styles.solverCompareHead}>
            Solver comparison — pick the fit to keep
          </div>
          <table className={styles.solverCompareTable}>
            <thead>
              <tr>
                <th scope="col">Solver</th>
                <th scope="col">Fit RMSE</th>
                <th scope="col">Exponent m</th>
                <th scope="col">λBC</th>
                <th scope="col">NRMSE</th>
                <th scope="col">Max resid.</th>
              </tr>
            </thead>
            <tbody>
              {comparedMethods.map(m => {
                const r = methodResults?.[m];
                const values = r?.sample?.analysis_values?.lambda_values;
                const oc = r?.sample?.analysis_outcomes?.lambda;
                const failed = !r || r.status === 'failed' || !values;
                const isChosen = lambda?.method === m;
                return (
                  <tr
                    key={m}
                    className={isChosen ? styles.solverRowChosen : undefined}
                    aria-selected={isChosen}
                  >
                    <td>
                      <label className={styles.solverRowLabel}>
                        <input
                          type="radio"
                          name={`solver-${sample.uid}`}
                          checked={isChosen}
                          disabled={failed || !onChooseMethod}
                          onChange={() => onChooseMethod?.(sample.uid, m)}
                        />
                        {LAMBDA_METHOD_LABELS[m]}
                        {m === bestMethod && !failed ? (
                          <span className={styles.solverBestBadge}>best</span>
                        ) : null}
                      </label>
                    </td>
                    <td>{failed ? '—' : formatNumber(values?.fit_rmse, 2)}</td>
                    <td>
                      {failed
                        ? '—'
                        : formatNumber(values?.lambda_value)}
                    </td>
                    <td>
                      {failed
                        ? '—'
                        : formatNumber(
                            toBrooksCoreyPoreSizeDistributionIndex(
                              values?.lambda_value
                            )
                          )}
                    </td>
                    <td>{failed ? 'failed' : formatNumber(oc?.fit_nrmse)}</td>
                    <td>
                      {failed ? '—' : formatNumber(oc?.fit_max_residual, 2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {isKrMethod(method) ? (
        (() => {
          const kr =
            method === 'water-oil'
              ? enriched.k_rel_values?.water_oil
              : enriched.k_rel_values?.gas_oil;
          const wo = method === 'water-oil' ? enriched.k_rel_values?.water_oil : null;
          const go = method === 'gas-oil' ? enriched.k_rel_values?.gas_oil : null;
          const sor = wo ? wo.true_sor_w ?? wo.sor_w : go?.true_sor_g ?? go?.sor_g;
          const no = wo ? wo.true_no_w ?? wo.no_w : go?.true_no_g ?? go?.no_g;
          const secondExp = wo ? wo.true_nw ?? wo.nw : go?.true_ng ?? go?.ng;
          const endpoint = wo
            ? wo.true_krw ?? wo.krw_exponent
            : go?.true_krg ?? go?.krg_exponent;
          const kroEndpoint = wo ? wo.true_kro_w : go?.true_kro_g;
          if (!kr) return null;
          return (
            <dl className={styles.detailParams}>
              <div>
                <dt>{method === 'water-oil' ? 'Sor' : 'Sorg'}</dt>
                <dd>{formatNumber(sor, 3)}</dd>
              </div>
              <div>
                <dt>No</dt>
                <dd>{formatNumber(no, 3)}</dd>
              </div>
              <div>
                <dt>{method === 'water-oil' ? 'Nw' : 'Ng'}</dt>
                <dd>{formatNumber(secondExp, 3)}</dd>
              </div>
              <div>
                <dt>{method === 'water-oil' ? "Krw'" : "Krg'"}</dt>
                <dd>{formatNumber(endpoint, 3)}</dd>
              </div>
              <div>
                <dt>Kro&apos;</dt>
                <dd>{formatNumber(kroEndpoint, 3)}</dd>
              </div>
              <div>
                <dt>Swi</dt>
                <dd>{formatNumber(enriched.input_sample_values?.swi, 3)}</dd>
              </div>
              <div>
                <dt>{method === 'water-oil' ? 'No+Nw' : 'No+Ng'}</dt>
                <dd>{formatNumber(outcome?.exponent_sum, 2)}</dd>
              </div>
              <div>
                <dt>R² (Sor)</dt>
                <dd>{formatNumber(outcome?.r_squared)}</dd>
              </div>
              <div>
                <dt>R² (endpoint)</dt>
                <dd>{formatNumber(outcome?.secondary_r_squared)}</dd>
              </div>
              <div>
                <dt>{method === 'water-oil' ? 'Sw@Kro=Krw' : 'Sg@Kro=Krg'}</dt>
                <dd>{formatNumber(outcome?.crossover_saturation, 3)}</dd>
              </div>
              <div>
                <dt>Points</dt>
                <dd>
                  {outcome?.n_fit_points ?? '—'}
                  {outcome?.n_total_points != null
                    ? ` / ${outcome.n_total_points}`
                    : ''}
                </dd>
              </div>
            </dl>
          );
        })()
      ) : method === 'lambda' ? (
        <dl className={styles.detailParams}>
          <div>
            <dt>Pe</dt>
            <dd>{formatNumber(lambda?.pe)}</dd>
          </div>
          <div>
            <dt>{BROOKS_COREY_EXPONENT_LABEL}</dt>
            <dd>{formatNumber(lambda?.lambda_value)}</dd>
          </div>
          <div>
            <dt>{BROOKS_COREY_PORE_SIZE_INDEX_LABEL}</dt>
            <dd>
              {formatNumber(
                toBrooksCoreyPoreSizeDistributionIndex(lambda?.lambda_value)
              )}
            </dd>
          </div>
          <div>
            <dt>Solver</dt>
            <dd>
              {lambda?.method ? LAMBDA_METHOD_LABELS[lambda.method] : '—'}
            </dd>
          </div>
          <div>
            <dt>Swi</dt>
            <dd>{formatNumber(enriched.input_sample_values?.swi)}</dd>
          </div>
          <div>
            <dt>Ln(Swn) Sum</dt>
            <dd>{formatNumber(lambda?.ln_swn_sum)}</dd>
          </div>
          <div>
            <dt>Ln(Pc) Sum</dt>
            <dd>{formatNumber(lambda?.ln_pc_sum)}</dd>
          </div>
          <div>
            <dt>R²</dt>
            <dd>{formatNumber(outcome?.r_squared)}</dd>
          </div>
          <div>
            <dt>NRMSE</dt>
            <dd>{formatNumber(outcome?.fit_nrmse)}</dd>
          </div>
          <div>
            <dt>Max resid.</dt>
            <dd>{formatNumber(outcome?.fit_max_residual, 2)}</dd>
          </div>
          <div>
            <dt>Fit RMSE</dt>
            <dd>{formatNumber(lambda?.fit_rmse, 2)}</dd>
          </div>
          <div>
            <dt>Points</dt>
            <dd>
              {outcome?.n_fit_points ?? '—'}
              {outcome?.n_total_points != null
                ? ` / ${outcome.n_total_points}`
                : ''}
            </dd>
          </div>
        </dl>
      ) : (
        <dl className={styles.detailParams}>
          <div>
            <dt>a</dt>
            <dd>{formatNumber(liao?.best_a)}</dd>
          </div>
          <div>
            <dt>b</dt>
            <dd>{formatNumber(liao?.best_b, 2)}</dd>
          </div>
          <div>
            <dt>Pce</dt>
            <dd>{formatNumber(liao?.pce)}</dd>
          </div>
          <div>
            <dt>Swir</dt>
            <dd>{formatNumber(liao?.swir)}</dd>
          </div>
          <div>
            <dt>Pc max</dt>
            <dd>{formatNumber(liao?.pc_max, 2)}</dd>
          </div>
          <div>
            <dt>Lambda</dt>
            <dd>{formatNumber(liao?.lambda_variable)}</dd>
          </div>
          <div>
            <dt>FZI</dt>
            <dd>{formatNumber(liao?.fzi)}</dd>
          </div>
          <div>
            <dt>Fit RMSE</dt>
            <dd>{formatNumber(outcome?.fit_rmse)}</dd>
          </div>
        </dl>
      )}

      <div className={styles.detailActions}>
        <Button
          variant="secondary"
          onClick={() => onRerun(sample.uid)}
          disabled={rerunDisabled}
        >
          Rerun sample
        </Button>
        <Button
          variant="secondary"
          onClick={() => onToggleReviewed(sample.uid)}
          disabled={!result}
        >
          {result?.reviewed ? 'Unmark reviewed' : 'Mark reviewed'}
        </Button>
        <Button
          variant="primary"
          onClick={() => onOpenInCapillaryPressure(sample.uid)}
        >
          {isKrMethod(method)
            ? 'Open in Kr Analysis'
            : 'Open in Capillary Pressure'}
        </Button>
      </div>
    </div>
  );
};

export default SampleDetailPanel;
